"use client"

import {
  CalendarClockIcon,
  HomeIcon,
  ImagesIcon,
  InfoIcon,
  ReceiptTextIcon,
  SearchIcon,
  ShoppingCartIcon,
  SparklesIcon,
  UserIcon,
  type LucideIcon,
} from "lucide-react"

import { cn } from "@workspace/ui/lib/utils"

import { Link, usePathname } from "@/i18n/navigation"

/**
 * The `express` layout's navigation — one destination list rendered two ways:
 * the sidebar column on `lg`+ and the fixed bottom tab bar below it.
 *
 * A CLIENT component on purpose. Next.js does not re-render a shared layout on
 * client-side navigation, so an active-link state computed on the server would
 * freeze at whatever route was rendered first and only correct itself on a hard
 * refresh (the same reason `FooterHider` is a client component). `usePathname`
 * here is the locale-free route, so the comparison needs no locale stripping.
 *
 * Items arrive as plain data — icons are named, not passed as components, since
 * a component cannot cross the server→client boundary as a prop. Labels are
 * already translated by the caller; this file holds no i18n.
 */
export type ExpressNavIconName =
  | "home"
  | "menu"
  | "offers"
  | "orders"
  | "book"
  | "account"
  | "info"
  | "gallery"
  | "cart"

const NAV_ICONS: Record<ExpressNavIconName, LucideIcon> = {
  home: HomeIcon,
  menu: SearchIcon,
  offers: SparklesIcon,
  orders: ReceiptTextIcon,
  book: CalendarClockIcon,
  account: UserIcon,
  info: InfoIcon,
  gallery: ImagesIcon,
  cart: ShoppingCartIcon,
}

export interface ExpressNavItem {
  href: string
  label: string
  icon: ExpressNavIconName
  /** Rendered as a count badge on the icon (basket tab). */
  badge?: number
}

/** `/menu` should stay active on `/menu/[itemId]`; `/` only matches exactly. */
function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/"
  return pathname === href || pathname.startsWith(`${href}/`)
}

export function ExpressSidebarNav({
  items,
  label,
}: {
  items: ExpressNavItem[]
  /** Translated `aria-label` for the nav landmark. */
  label: string
}) {
  const pathname = usePathname()

  return (
    <nav aria-label={label} className="flex flex-col gap-1 p-3">
      {items.map((item) => {
        const Icon = NAV_ICONS[item.icon]
        const active = isActive(pathname, item.href)
        return (
          <Link
            key={item.href}
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-4xl px-4 py-2.5 text-sm font-medium transition-colors",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon className="size-5 shrink-0" aria-hidden />
            <span className="truncate">{item.label}</span>
            {item.badge ? (
              <span className="ms-auto rounded-4xl bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                {item.badge}
              </span>
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}

export function ExpressBottomNav({
  items,
  label,
}: {
  items: ExpressNavItem[]
  label: string
}) {
  const pathname = usePathname()

  return (
    <nav
      aria-label={label}
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden"
    >
      <ul className="flex items-stretch justify-around">
        {items.map((item) => {
          const Icon = NAV_ICONS[item.icon]
          const active = isActive(pathname, item.href)
          return (
            <li key={item.href} className="flex-1">
              <Link
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center gap-1 px-1 py-2 text-[0.6875rem] font-medium transition-colors",
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="relative">
                  <Icon className="size-5" aria-hidden />
                  {item.badge ? (
                    <span
                      className="absolute -end-2 -top-1.5 min-w-4 rounded-4xl bg-primary px-1 text-[0.625rem] leading-4 font-semibold text-primary-foreground"
                      aria-hidden
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </span>
                <span className="max-w-full truncate">{item.label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
